import { Select } from "@openanalyst-ai/ui/select"
import { createEffect, createMemo, createResource, createSignal, onCleanup, Show, type Component } from "solid-js"
import { useGlobalSDK } from "@/context/global-sdk"
import { usePlatform } from "@/context/platform"
import { refreshTenxMemoryRuntimeContextForWorkspace } from "@/pages/session/tenxmemory-runtime-context"
import {
  productSuiteActiveHandleOptions,
  productSuiteActiveSelectableHandle,
  productSuiteCredentialsClient,
  productSuiteHandleHost,
  productSuiteProjectHandle,
  productSuiteSelectedActiveHandle,
  type ProductSuiteCredentials,
  type ProductSuiteHandle,
} from "./settings-product-suite-model"

const HANDLE_CHANGE_EVENT = "openanalyst-product-suite-handle-change"

// Race the switch against a deadline: if the backend is unreachable the update
// can hang, which would otherwise leave the titlebar pill stuck disabled.
const SWITCH_TIMEOUT_MS = 20_000
function withTimeout<T>(promise: Promise<T>, ms = SWITCH_TIMEOUT_MS): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error("Product Suite handle switch timed out")), ms)),
  ])
}

export function notifyProductSuiteProjectHandleChange(input: { projectPath: string; handle: string }) {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(HANDLE_CHANGE_EVENT, { detail: input }))
}

export function listenProductSuiteProjectHandleChange(
  listener: (input: { projectPath?: string; handle?: string }) => void,
) {
  if (typeof window === "undefined") return () => undefined
  const onChange = (event: Event) => listener((event as CustomEvent<{ projectPath?: string; handle?: string }>).detail ?? {})
  window.addEventListener(HANDLE_CHANGE_EVENT, onChange)
  return () => window.removeEventListener(HANDLE_CHANGE_EVENT, onChange)
}

export const TitlebarProductSuiteHandle: Component<{ projectPath?: string }> = (props) => {
  const globalSDK = useGlobalSDK()
  const platform = usePlatform()
  const [saving, setSaving] = createSignal("")
  const credentialsClient = () => productSuiteCredentialsClient(globalSDK.client)
  const projectPath = createMemo(() => props.projectPath?.trim() ?? "")
  const [credentials, actions] = createResource(projectPath, async (path): Promise<ProductSuiteCredentials | undefined> => {
    if (!path) return undefined
    return (await credentialsClient().get()).data
  })
  const status = createMemo(() => credentials.latest)
  const handles = createMemo(() => productSuiteActiveHandleOptions(status()))
  const activeHandle = createMemo(() => productSuiteActiveSelectableHandle(status(), projectPath()))
  const selectedHandle = createMemo(() => productSuiteSelectedActiveHandle(status(), projectPath()))
  // Explicit binding only (projectHandles map) - the fallback-resolved handle
  // never counts as bound. A bound project's handle is LOCKED here: it can
  // only be changed or removed in Settings -> Product Suite.
  const boundHandle = createMemo(() => productSuiteProjectHandle(status(), projectPath()))
  const showPill = createMemo(() => !!projectPath() && !credentials.error && (handles().length > 0 || !!activeHandle()))

  createEffect(() => {
    const stop = listenProductSuiteProjectHandleChange((input) => {
      if (input.projectPath && input.projectPath !== projectPath()) return
      void actions.refetch()
    })
    onCleanup(stop)
  })

  // Keep the engine runtime context in step with the project's resolved handle
  // on project switches - WITHOUT writing a binding. Bindings are only created
  // by an explicit user selection (here, when unbound) or in Product Suite;
  // the previous auto-persist here silently bound every visited project.
  let refreshedRuntimeKey = ""
  createEffect(() => {
    const path = projectPath()
    const handle = activeHandle()
    if (!path || !handle || credentials.loading || credentials.error) return
    const key = `${path}\n${handle}`
    if (refreshedRuntimeKey === key) return
    refreshedRuntimeKey = key
    void refreshTenxMemoryRuntimeContextForWorkspace(platform, path, { handle })
  })

  const selectHandle = (item: ProductSuiteHandle | undefined) => {
    const path = projectPath()
    const value = item?.handle.trim()
    // Switching from the titlebar re-binds the project to the chosen handle. It must
    // stay switchable after the first pick (previously a bound project locked the
    // pill, so a second switch silently no-op'd). Only skip a no-op re-selection.
    if (!path || !value || value === boundHandle() || value === activeHandle() || saving()) return
    setSaving(value)
    void withTimeout(credentialsClient().update({ projectPath: path, handle: value }))
      .then((result) => {
        actions.mutate(result.data)
        notifyProductSuiteProjectHandleChange({ projectPath: path, handle: value })
        void refreshTenxMemoryRuntimeContextForWorkspace(platform, path, { handle: value })
      })
      .catch((error) => console.error("[titlebar:product-suite] handle switch failed", error))
      .finally(() => setSaving(""))
  }

  return (
    <Show when={showPill()}>
      <div class="hidden shrink-0 items-center gap-1 ml-4 pr-1 lg:flex">
        <Select
          options={handles()}
          current={selectedHandle()}
          value={(item) => item.handle}
          label={(item) => productSuiteHandleHost(item.handle)}
          onSelect={selectHandle}
          disabled={handles().length === 0 || !!saving()}
          variant="secondary"
          size="small"
          valueClass="max-w-[142px]"
          placeholder="No handle"
          triggerProps={{
            "aria-label": "Project Product Suite handle",
            "data-titlebar-product-suite-handle": true,
            title: boundHandle()
              ? `${productSuiteHandleHost(boundHandle())} — bound to this project. Manage in Settings → Product Suite.`
              : activeHandle()
                ? productSuiteHandleHost(activeHandle())
                : "No handle",
          }}
          triggerIconStyle={{
            color: "#000",
            opacity: 1,
          }}
          triggerIconSvgStyle={{
            color: "#000",
            "stroke-width": "1.25",
          }}
          triggerStyle={{
            height: "24px",
            "min-width": "122px",
            "max-width": "184px",
            "border-radius": "7px",
            border: "1px solid color-mix(in srgb, var(--surface-brand-base) 72%, var(--border-strong-base))",
            "background-color": "var(--surface-brand-base)",
            color: "var(--text-on-brand-strong)",
            "box-shadow": "0 1px 2px rgba(15, 23, 42, 0.08)",
            "font-size": "12px",
            "font-weight": 650,
            padding: "0 6px 0 10px",
            gap: "6px",
          }}
        />
      </div>
    </Show>
  )
}
